import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { deletePayment } from '../../api/payments';
import { Payment } from '../../types/payment';

const errorText = (err: unknown) =>
  isAxiosError(err)
    ? ((err.response?.data as { message?: string } | undefined)?.message ??
      "Couldn't delete the payment")
    : "Couldn't delete the payment";

/** Delete a pending or rejected payment (2026-11-04) — confirmation with a
 * required reason. The payment is kept as Deleted with its full history;
 * approved payments are reversed instead (the server refuses otherwise). */
export const DeletePaymentDialog = ({
  payment,
  onClose,
  onDeleted,
}: {
  payment: { id: number; paymentNumber: string } | null;
  onClose: () => void;
  onDeleted: (payment: Payment) => void;
}) => {
  const [reason, setReason] = useState('');
  const mutation = useMutation({
    mutationFn: () => deletePayment(payment!.id, reason.trim()),
    onSuccess: (payment) => {
      setReason('');
      onDeleted(payment);
    },
  });
  const close = () => {
    if (mutation.isPending) return;
    setReason('');
    mutation.reset();
    onClose();
  };

  return (
    <Dialog open={!!payment} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle>Delete {payment?.paymentNumber}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Are you sure you want to delete this payment record? This action will be recorded in the
          audit history.
        </Typography>
        <TextField
          label="Reason for deleting"
          helperText="Required — kept with the payment and in the audit history."
          fullWidth
          multiline
          minRows={2}
          autoFocus
          inputProps={{ maxLength: 500 }}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        {mutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorText(mutation.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={!reason.trim() || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Deleting…' : 'Delete payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
